import { Component, Input, OnInit } from '@angular/core';
import { ElementType } from 'src/app/_objects/element-type';
import { FromToDetail } from 'src/app/_objects/report-template';
import { MousePoint, Notifier, ReportTemplateDesignService } from 'src/app/_services/report-template-design.service';
import { ReportSubTotal } from '../../_objects/report-subtotal';

@Component({
    selector: 'app-report-subtotal-container',
    templateUrl: './report-subtotal.component.html',
    styleUrls: ['./report-subtotal.component.scss'],
    standalone: false
})
export class ReportSubTotalComponent implements OnInit {

  @Input() public pageKey: string;
  @Input() public subTotalKey: string;
  @Input() public elementLocation: string;

  public reportSubTotal: ReportSubTotal;
  private notifier: Notifier;
  public resizeMarkerSize: number;
  public resizing: boolean;

  constructor(public designService: ReportTemplateDesignService) {
    this.resizeMarkerSize = 36;
    this.resizing = false;
  }

  public ngOnInit() {
    this.notifier = {
      componentName: 'report-subtotal.component',
      ids: { pageId: this.pageKey, elementId: this.subTotalKey }
    };
    this.reportSubTotal = this.designService.getSubTotal(this.subTotalKey);
    let elementNavigator = this.designService.navigatorElements
    .filter((x) => x.pageKey == this.pageKey)[0]
    .elementsDetails.filter((y) => y.location == this.elementLocation)[0]
      .elements;

    elementNavigator.filter(x => x.elementName == ElementType.SubTotal).length > 0
      ? elementNavigator.filter(x => x.elementName == ElementType.SubTotal)[0].elementData.push({
        id: this.subTotalKey,
          page: this.pageKey,
          name: this.reportSubTotal.getTitle(),
        })
      : elementNavigator.push({
          elementName: ElementType.SubTotal,
          isVisible: false,
          elementData: [
            {
              id: this.subTotalKey,
              page: this.pageKey,
              name: this.reportSubTotal.getTitle(),
            },
          ],
        });
  }

  public onClick(event: MouseEvent) {
    if (this.reportSubTotal.isSelectable) {
      this.designService.notifySelectElement(this.notifier, this.subTotalKey, event.ctrlKey);
      event.stopPropagation();
    }
  }

  public onDragStart(event: DragEvent) {
    // console.log('ReportSubTotal onDragStart');
    event.dataTransfer.setData('text', this.subTotalKey);
    event.dataTransfer.effectAllowed = 'move';

    const fromDetail: FromToDetail = {
      location: this.reportSubTotal.getLocation(),
      pageId: this.pageKey
    };
    const dragPoint: MousePoint = {
      onElementX: event.offsetX,
      onElementY: event.offsetY
    };

    this.designService.notifyBeginDrag(this.notifier, ElementType.SubTotal, fromDetail, dragPoint);
  }

  public onDragOver(event: DragEvent) {
    // console.log('ReportSubTotal onDragOver');
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    event.stopPropagation();
  }

  public onDrop(event: DragEvent) {
    // console.log('ReportSubTotal onDrop');
    event.preventDefault();
    const template  = {...this.designService.originTemplate};
    template.reportTemplateContent = JSON.stringify(this.designService.getTemplate());
    this.designService.templateForUndo.push(template);
    const dragElemId = event.dataTransfer.getData('text');

    const toDetail: FromToDetail = {
      location: this.reportSubTotal.getLocation(),
      pageId: this.pageKey
    };
    const dropPoint: MousePoint = {
      onElementX: this.reportSubTotal.getLeftPx() + event.offsetX,
      onElementY: this.reportSubTotal.getTopPx() + event.offsetY
    };

    this.designService.notifyDrop(this.notifier, dragElemId, toDetail, dropPoint);

    event.dataTransfer.clearData('text');
    event.stopPropagation();
  }

  public getResizeMarkerSize(): string {
    return this.resizeMarkerSize + 'px';
  }

  public getTitleResizeMarkerLeft(horizontal: string): string {
    switch (horizontal) {
      case 'left':
        return (this.resizeMarkerSize / 2 * -1) + 'px';
      case 'center':
        return ((this.reportSubTotal.getTitleWidthPx() / 2) - (this.resizeMarkerSize / 2)) + 'px';
      case 'right':
        return (this.reportSubTotal.getTitleWidthPx() - (this.resizeMarkerSize / 2)) + 'px';
      default:
        return '';
    }
  }

  public getTitleResizeMarkerTop(vertical: string): string {
    switch (vertical) {
      case 'top':
        return (this.resizeMarkerSize / 2 * -1) + 'px';
      case 'middle':
        return ((this.reportSubTotal.getTitleHeightPx() / 2) - (this.resizeMarkerSize / 2)) + 'px';
      case 'bottom':
        return (this.reportSubTotal.getTitleHeightPx() - (this.resizeMarkerSize / 2)) + 'px';
      default:
        return '';
    }
  }

  public getTableResizeMarkerLeft(horizontal: string): string {
    switch (horizontal) {
      case 'left':
        return (this.resizeMarkerSize / 2 * -1) + 'px';
      case 'center':
        return ((this.reportSubTotal.getTableWidthPx() / 2) - (this.resizeMarkerSize / 2)) + 'px';
      case 'right':
        return (this.reportSubTotal.getTableWidthPx() - (this.resizeMarkerSize / 2)) + 'px';
      default:
        return '';
    }
  }

  public getTableResizeMarkerTop(vertical: string): string {
    switch (vertical) {
      case 'top':
        return (this.resizeMarkerSize / 2 * -1) + 'px';
      case 'middle':
        return ((this.reportSubTotal.getTableHeightPx() / 2) - (this.resizeMarkerSize / 2)) + 'px';
      case 'bottom':
        return (this.reportSubTotal.getTableHeightPx() - (this.resizeMarkerSize / 2)) + 'px';
      default:
        return '';
    }
  }

  public onResizeMarkerDown(event: MouseEvent, vertical: string, horizontal: string) {
    event.preventDefault();
    this.resizing = true;
    const beginPoint: MousePoint = {
      onElementX: event.offsetX,
      onElementY: event.offsetY
    };
    this.designService.notifyBeginResize(this.notifier, ElementType.Text, horizontal, vertical, beginPoint);

    event.stopPropagation();
  }

  public onTitleResizing(event: MouseEvent) {
    event.preventDefault();
    if (this.resizing) {
      this.designService.notifyResize(this.notifier, { onElementX: event.offsetX, onElementY: event.offsetY });
    }
    event.stopPropagation();
  }

  public onTableResizing(event: MouseEvent) {
    this.reportSubTotal.tableResize = true
    event.preventDefault();
    if (this.resizing) {
      this.designService.notifyResize(this.notifier, { onElementX: event.offsetX, onElementY: event.offsetY });
    }
    event.stopPropagation();
  }

  public onResizeMarkerUp(event: MouseEvent) {
    event.preventDefault();
    this.resizing = false;
    this.reportSubTotal.tableResize = false
    event.stopPropagation();
  }

  public onResizeMarkerLeave(event: MouseEvent) {
    event.preventDefault();
    this.resizing = false;
    this.reportSubTotal.tableResize = false
    event.stopPropagation();
  }

  replaceLabel(value: string): string {
    return value.replace('objects.', '');
  }
}
